/** Whether Telegram exposes a BiometricManager object at all (sync, coarse). */
export const biometricAvailable = () => !!window.Telegram?.WebApp?.BiometricManager;

/**
 * Accurate support check — inits the manager and reads `isBiometricAvailable`
 * (false on Telegram Desktop / clients without biometric hardware). Never hangs.
 */
export function biometricSupported(): Promise<boolean> {
  return new Promise((resolve) => {
    const bm = window.Telegram?.WebApp?.BiometricManager;
    if (!bm) return resolve(false);
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      resolve(!!bm.isBiometricAvailable);
    };
    const timer = setTimeout(finish, 3000);
    if (bm.isInited) finish();
    else bm.init(finish);
  });
}

/** Run the Telegram biometric prompt; resolves true on success, false otherwise.
 *  Short-circuits when biometric isn't available and times out so it never hangs. */
export function runBiometric(reason: string): Promise<boolean> {
  return new Promise((resolve) => {
    const bm = window.Telegram?.WebApp?.BiometricManager;
    if (!bm) return resolve(false);

    let done = false;
    const finish = (ok: boolean) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      resolve(ok);
    };
    const timer = setTimeout(() => finish(false), 30000);

    const authenticate = () => bm.authenticate({ reason }, (ok) => finish(ok));
    const access = () => {
      if (!bm.isBiometricAvailable) return finish(false);
      if (bm.isAccessGranted) return authenticate();
      bm.requestAccess({ reason }, (granted) => (granted ? authenticate() : finish(false)));
    };

    if (bm.isInited) access();
    else bm.init(access);
  });
}
